// iter-121.117 — OA "For Your Action" lane
//
// 6 tonal tiles, all sourced from `/api/home/oa/actions`. Per user
// direction (option 4b in plan), we DON'T show an "All clear" hero —
// all 6 tiles are always rendered, with zero-count tiles dimmed.
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useOaScope } from "@/lib/oa-scope";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, Plug, Mail, UserX, Hourglass, FileWarning, Database,
    ArrowUpRight, ChevronRight,
} from "lucide-react";
import OaActionDrawer from "./OaActionDrawer";

const ACTION_META = {
    schema_mapping_pending: { Icon: Database,      tone: "warn" },
    ai_budget_warning:      { Icon: FileWarning,   tone: "warn" },
    connectors_failing:     { Icon: Plug,          tone: "critical" },
    pending_invites:        { Icon: Mail,          tone: "info" },
    stalled_signups:        { Icon: UserX,         tone: "warn" },
    trial_ending_soon:      { Icon: Hourglass,     tone: "critical" },
};

function toneClasses(tone) {
    switch (tone) {
        case "critical":
            return {
                ring: "border-destructive/40",
                bg: "bg-destructive/5 hover:bg-destructive/10",
                icon: "text-destructive",
                badge: "bg-destructive/15 text-destructive border-destructive/30",
            };
        case "warn":
            return {
                ring: "border-amber-500/40",
                bg: "bg-amber-500/5 hover:bg-amber-500/10",
                icon: "text-amber-600 dark:text-amber-400",
                badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
            };
        default:
            return {
                ring: "border-border",
                bg: "bg-secondary/40 hover:bg-secondary/60",
                icon: "text-foreground",
                badge: "bg-secondary text-foreground border-border",
            };
    }
}

function ActionTile({ item, onOpen }) {
    const meta = ACTION_META[item.key] || { Icon: AlertTriangle, tone: "info" };
    const cls = toneClasses(meta.tone);
    const { Icon } = meta;
    const zero = (item.count || 0) === 0;
    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className={`text-left border rounded-sm p-3 flex flex-col gap-2 transition-colors ${zero ? "border-border bg-card/60 opacity-60 hover:opacity-100" : `${cls.ring} ${cls.bg}`}`}
            data-testid={`oa-action-tile-${item.key}`}
            data-state={zero ? "zero" : "active"}
        >
            <div className="flex items-start justify-between gap-2">
                <Icon className={`h-5 w-5 ${zero ? "text-muted-foreground" : cls.icon}`} strokeWidth={1.5} />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-baseline justify-between gap-2">
                <span className={`kpi-number text-2xl leading-none font-semibold ${zero ? "text-muted-foreground" : ""}`}>
                    {item.count}
                </span>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-wider ${zero ? "bg-secondary text-muted-foreground border-border" : cls.badge}`}>
                    {item.severity}
                </span>
            </div>
            <div className="text-[12px] font-medium leading-tight">{item.label}</div>
            <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{item.hint}</div>
            <div className={`text-[10px] font-mono uppercase tracking-wider mt-1 inline-flex items-center gap-1 ${zero ? "text-muted-foreground" : "text-primary"}`}>
                {item.cta_label} <ArrowUpRight className="h-3 w-3" />
            </div>
        </button>
    );
}

export default function OaActionLane({ onEmpty }) {
    const { queryString } = useOaScope();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        try {
            const r = await api.get(`/home/oa/actions?${queryString}`);
            setData(r.data || null);
        } catch {
            setData({ items: [], total_action_count: 0, all_clear: true });
        }
    }, [queryString]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (onEmpty && data) onEmpty(false);  // we always render all 6 tiles
    }, [data, onEmpty]);

    if (data === null) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
                 data-testid="oa-action-lane-loading">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-32 shimmer rounded-sm" />
                ))}
            </div>
        );
    }

    return (
        <div data-testid="oa-action-lane">
            <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-[10px]">For Your Action</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {data.total_action_count} {data.total_action_count === 1 ? "signal" : "signals"}
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {(data.items || []).map((it) => (
                    <ActionTile key={it.key} item={it} onOpen={setOpen} />
                ))}
            </div>

            <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
                <SheetContent className="sm:max-w-xl w-full" data-testid="oa-action-drawer-sheet">
                    {open && (
                        <>
                            <SheetHeader>
                                <SheetTitle>{open.label}</SheetTitle>
                                <SheetDescription>{open.hint}</SheetDescription>
                            </SheetHeader>
                            <div className="mt-4">
                                <OaActionDrawer
                                    item={open}
                                    onClose={() => setOpen(null)}
                                    onNavigate={(to) => { setOpen(null); navigate(to); }}
                                />
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
